/**
 * Home page behavior: the day hero's hour cycle, the open institution's film and
 * worn-photo strip, and the footer masthead's walk through the town. The page reads
 * fine without it: the first hour, a still frame and the first institution show.
 */

const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
const reduced = () => reducedMotion?.matches === true;

const playVideo = (video) => {
  if (!video || reduced()) return;
  const playing = video.play();
  if (playing) playing.catch(() => {});
};

// A day in Halldale: one viewport, each hour in turn.
const setupHomeDay = (root) => {
  if (root.dataset.homeDayInitialized === 'true') return;
  root.dataset.homeDayInitialized = 'true';

  const frames = [...root.querySelectorAll('[data-home-day-frame]')];
  const hours = [...root.querySelectorAll('[data-home-day-hour]')];
  const stops = [...root.querySelectorAll('[data-home-day-stop]')];
  const time = root.querySelector('[data-home-day-time]');
  const meridiem = root.querySelector('[data-home-day-meridiem]');
  const pause = root.querySelector('[data-home-day-pause]');
  const dwell = Number(root.dataset.dwell || 7) * 1000;
  const labels = stops.map((stop) => {
    const [clock = '', half = ''] = (stop.querySelector('.home-day__stop-time')?.textContent ?? '').trim().split(/\s+/);
    return { clock, half };
  });

  let index = 0;
  let timer = 0;
  let fadeTimer = 0;
  let leaving = null;
  let waiting = null;
  let visible = false;
  let paused = reduced();
  const active = () => visible && document.visibilityState === 'visible';
  // The crossfade in sections/home-day.liquid, and the longest the current hour
  // holds while the next video gets ready before the page shows its still instead.
  const fade = () => (reduced() ? 0 : 1400);
  const hold = 4000;

  const video = (at) => frames[at]?.querySelector('[data-home-day-video]');
  const rewind = (clip) => {
    if (!clip) return;
    clip.pause();
    if (clip.currentTime > 0) clip.currentTime = 0;
  };
  // Has enough of the video to start moving at once.
  const ready = (clip) => !clip || clip.readyState >= 3;
  // Abandons a pending fade; the hidden video stops unless it is about to show.
  const stopWaiting = (keep = false) => {
    if (!waiting) return;
    waiting.clip?.removeEventListener('playing', waiting.go);
    window.clearTimeout(waiting.timer);
    if (!keep) rewind(waiting.clip);
    waiting = null;
  };
  const settleFade = () => {
    window.clearTimeout(fadeTimer);
    if (leaving === null) return;
    frames[leaving].classList.remove('is-leaving');
    rewind(video(leaving));
    leaving = null;
  };
  const stopPlayback = () => {
    stopWaiting();
    // Settle the visual transition as well as the media: a paused hero must
    // never leave an old frame visible over the selected hour.
    root.classList.add('is-fade-settled');
    settleFade();
    video(index)?.pause();
  };

  const schedule = () => {
    window.clearTimeout(timer);
    root.classList.toggle('is-playing', !paused && active());
    if (paused || !active() || frames.length < 2) return;
    timer = window.setTimeout(advance, dwell);
  };

  // Start the next hour underneath and fade to it once it moves, so the page never
  // sits on a frozen frame. The current hour keeps playing while it waits.
  const advance = () => {
    stopWaiting();
    const next = (index + 1) % frames.length;
    const clip = video(next);
    // When the browser blocks autoplay (Low Power Mode), the stills step on as usual.
    if (ready(clip) || video(index)?.paused) return show(next);
    const go = () => {
      stopWaiting(true);
      if (!paused && active()) show(next);
    };
    waiting = { clip, go, timer: window.setTimeout(go, hold) };
    clip.preload = 'auto';
    clip.addEventListener('playing', go);
    playVideo(clip);
  };

  const show = (next) => {
    stopWaiting(true);
    settleFade();
    const previous = index;
    index = (next + frames.length) % frames.length;
    if (!paused && active()) root.classList.remove('is-fade-settled');
    frames.forEach((frame, at) => {
      frame.classList.toggle('is-on', at === index);
      frame.classList.toggle('is-leaving', at === previous && at !== index);
      if (at !== index && at !== previous) rewind(video(at));
    });
    // The outgoing hour plays on under the incoming one until it is covered.
    if (previous !== index && !paused && active()) {
      leaving = previous;
      fadeTimer = window.setTimeout(() => {
        settleFade();
      }, fade());
    } else if (previous !== index) {
      frames[previous].classList.remove('is-leaving');
      rewind(video(previous));
    }
    hours.forEach((hour, at) => (hour.hidden = at !== index));
    stops.forEach((stop, at) => {
      stop.classList.toggle('is-on', at === index);
      stop.classList.toggle('is-past', at < index);
      const button = stop.querySelector('button');
      if (at === index) button?.setAttribute('aria-current', 'true');
      else button?.removeAttribute('aria-current');
    });
    // Restart the progress line on the current stop.
    root.classList.remove('is-playing');
    void root.offsetWidth;

    if (time && labels[index]) {
      time.classList.add('is-ticking');
      window.setTimeout(
        () => {
          time.textContent = labels[index].clock;
          if (meridiem) meridiem.textContent = labels[index].half;
          time.classList.remove('is-ticking');
        },
        reduced() ? 0 : 200,
      );
    }

    if (!paused && active()) playVideo(video(index));
    // Fetch the next hour's video ahead of time.
    const upcoming = video((index + 1) % frames.length);
    if (upcoming) upcoming.preload = 'auto';
    schedule();
  };

  const setPaused = (value) => {
    paused = value;
    root.classList.toggle('is-paused', paused);
    if (pause) pause.setAttribute('aria-label', paused ? pause.dataset.playLabel : pause.dataset.pauseLabel);
    if (paused) {
      stopPlayback();
    } else if (active()) playVideo(video(index));
    schedule();
  };

  root.querySelectorAll('[data-home-day-jump]').forEach((button) => {
    button.addEventListener('click', () => show(Number(button.dataset.homeDayJump)));
  });
  pause?.addEventListener('click', () => setPaused(!paused));

  new IntersectionObserver(
    ([entry]) => {
      visible = entry.isIntersecting;
      if (active() && !paused) playVideo(video(index));
      else {
        stopPlayback();
      }
      schedule();
    },
    { threshold: 0.3 },
  ).observe(root);

  document.addEventListener('visibilitychange', () => {
    if (active() && !paused) playVideo(video(index));
    else {
      stopPlayback();
    }
    schedule();
  });

  setPaused(paused);
  show(0);
};

// The open institution: its film plays while on screen; arrows page through the worn photos.
const setupInstitution = (root) => {
  if (root.dataset.homeInstitutionInitialized === 'true') return;
  root.dataset.homeInstitutionInitialized = 'true';

  const film = root.querySelector('[data-home-film]');
  if (film) {
    new IntersectionObserver(([entry]) => (entry.isIntersecting ? playVideo(film) : film.pause()), {
      threshold: 0.25,
    }).observe(film);
  }

  const strip = root.querySelector('[data-home-worn-strip]');
  if (strip) setupWornLoop(strip, root.querySelectorAll('[data-home-worn-step]'));
};

// The worn strip never ends: copies sit on either side of the real photos. Only the
// visible copy of a card is interactive; its original is inert until it reappears.
const setupWornLoop = (strip, buttons) => {
  const originals = [...strip.children];
  if (originals.length === 0) {
    buttons.forEach((button) => (button.disabled = true));
    return;
  }
  const step = () => {
    const [first, second] = strip.children;
    return second ? second.offsetLeft - first.offsetLeft : strip.clientWidth;
  };
  const scroll = (left, smooth) => strip.scrollTo({ left, behavior: smooth && !reduced() ? 'smooth' : 'auto' });

  // A short set fits in one viewport: repeating it would put two instances of
  // the same card on screen, one of them inert. Keep the real links only.
  if (originals.length < 2 || originals.length * step() <= strip.clientWidth) {
    const updateButtons = () => {
      const width = originals.at(-1).offsetLeft + originals.at(-1).offsetWidth - originals[0].offsetLeft;
      buttons.forEach((button) => (button.disabled = width <= strip.clientWidth));
    };
    buttons.forEach((button) => {
      button.addEventListener('click', () =>
        scroll(strip.scrollLeft + Number(button.dataset.homeWornStep) * step(), true),
      );
    });
    updateButtons();
    new ResizeObserver(updateButtons).observe(strip);
    return;
  }

  buttons.forEach((button) => {
    button.addEventListener('click', () =>
      scroll(strip.scrollLeft + Number(button.dataset.homeWornStep) * step(), true),
    );
  });
  const copies = () =>
    originals.map((item) => {
      const copy = item.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      copy.inert = true;
      copy.dataset.homeWornCopy = '';
      return copy;
    });
  const before = copies();
  const after = copies();
  strip.prepend(...before);
  strip.append(...after);

  const setWidth = () => originals[0].offsetLeft - before[0].offsetLeft;
  const syncLinks = () => {
    const left = strip.scrollLeft;
    const right = left + strip.clientWidth;
    const inView = (item) => item.offsetLeft < right && item.offsetLeft + item.offsetWidth > left;
    originals.forEach((original, index) => {
      // Keep exactly one link per product in the tab order. Offscreen cards retain
      // their original link so keyboard traversal can still reach every photo.
      const active = [before[index], after[index]].find(inView) ?? original;
      for (const item of [before[index], original, after[index]]) {
        item.inert = item !== active;
        if (item === active) item.removeAttribute('aria-hidden');
        else item.setAttribute('aria-hidden', 'true');
      }
    });
  };
  const settle = () => {
    const width = setWidth();
    if (!width) return;
    if (strip.scrollLeft < width) scroll(strip.scrollLeft + width);
    else if (strip.scrollLeft >= width * 2) scroll(strip.scrollLeft - width);
    syncLinks();
  };

  scroll(setWidth());
  syncLinks();
  let settling = 0;
  const onScroll = () => {
    syncLinks();
    window.clearTimeout(settling);
    settling = window.setTimeout(settle, 140);
  };
  strip.addEventListener('scroll', onScroll, { passive: true });
  strip.addEventListener('scrollend', settle);

  // Keep the same photo in view when the card width changes.
  let lastWidth = setWidth();
  new ResizeObserver(() => {
    const width = setWidth();
    if (!width || width === lastWidth) return;
    const position = (strip.scrollLeft - lastWidth) / lastWidth;
    lastWidth = width;
    scroll(width + position * width);
    syncLinks();
  }).observe(strip);
};

// The footer masthead walks through the town: each institution and its motto in turn.
const setupTownRotation = (masthead) => {
  if (masthead.dataset.townInitialized === 'true') return;
  masthead.dataset.townInitialized = 'true';

  const list = [...(masthead.querySelector('[data-town-list]')?.content.children ?? [])].map((entry) => ({
    name: entry.dataset.name,
    motto: entry.dataset.motto,
  }));
  const name = masthead.querySelector('[data-town-name]');
  const motto = masthead.querySelector('[data-town-motto]');
  const swaps = [...masthead.querySelectorAll('.site-footer__series-swap')];
  if (list.length < 2 || !name || !motto || reduced()) return;

  let index = 0;
  let held = false;
  masthead.addEventListener('mouseenter', () => (held = true));
  masthead.addEventListener('mouseleave', () => (held = false));

  window.setInterval(() => {
    if (held || document.visibilityState !== 'visible') return;
    index = (index + 1) % list.length;
    swaps.forEach((swap) => swap.classList.add('is-leaving'));
    window.setTimeout(() => {
      name.textContent = list[index].name;
      motto.textContent = list[index].motto;
      swaps.forEach((swap) => {
        swap.classList.remove('is-leaving');
        swap.classList.add('is-arriving');
      });
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => swaps.forEach((swap) => swap.classList.remove('is-arriving'))),
      );
    }, 600);
  }, 4200);
};

const initializeHome = (root = document) => {
  root.querySelectorAll('[data-home-day]').forEach(setupHomeDay);
  root.querySelectorAll('.home-institution').forEach(setupInstitution);
  root.querySelectorAll('[data-town-rotation]').forEach(setupTownRotation);
};

initializeHome();
document.addEventListener('shopify:section:load', (event) => initializeHome(event.target));
